import React, { useState, useEffect } from 'react';
import { 
  User, Mail, CreditCard, Crown, Calendar, 
  LogOut, Settings, ChevronRight, Shield,
  Sparkles, CheckCircle, AlertCircle, Loader2,
  ExternalLink, HelpCircle, Trash2, FileText, RotateCcw
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PricingModal from '../components/subscription/PricingModal';
import { getApiUrl, getCurrency, setCurrency, getSupportedCurrencies, getCurrencySymbol } from '../utils';
import { shouldUseStoreKit, openManageSubscriptions, restorePurchases, purchaseProduct, getLocalizedPrice } from '../utils/iosPurchases';

const API_URL = getApiUrl();

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [currency, setCurrencyState] = useState(getCurrency());
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [storageLoading, setStorageLoading] = useState(false);
  const [showStorageIncluded, setShowStorageIncluded] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/stripe/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(res.data);
    } catch (err) {
      console.log('No subscription found');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    // iOS: open Apple subscription management
    if (shouldUseStoreKit()) {
      await openManageSubscriptions();
      return;
    }
    // Web: open Stripe customer portal
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/stripe/customer-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      setError('Failed to open subscription portal');
      setPortalLoading(false);
    }
  };

  const handleUpgrade = () => {
    setShowPricingModal(true);
  };

  // Buy bulk top-up (iOS: StoreKit, Web: Stripe)
  const handleBuyBulkTopup = async () => {
    setBulkLoading(true);
    setError('');
    try {
      if (shouldUseStoreKit()) {
        await purchaseProduct('bulk-topup');
        window.location.reload();
      } else {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API_URL}/stripe/buy-bulk-topup`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.url) window.location.href = res.data.url;
      }
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.response?.data?.error || err.message || 'Failed to purchase top-up');
      }
    } finally {
      setBulkLoading(false);
    }
  };

  // Buy storage subscription (iOS: StoreKit, Web: Stripe)
  const handleBuyStorage = async (storageTier) => {
    // Block if user already has Premium — unlimited storage is included
    const currentTier = getTierInfo().tier;
    if (currentTier === 'Premium') {
      setShowStorageIncluded(true);
      return;
    }
    setStorageLoading(true);
    setError('');
    try {
      if (shouldUseStoreKit()) {
        await purchaseProduct(`storage-${storageTier}`);
        window.location.reload();
      } else {
        const token = localStorage.getItem('token');
        const res = await axios.post(`${API_URL}/stripe/buy-storage`, { tier: storageTier }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.url) window.location.href = res.data.url;
      }
    } catch (err) {
      if (err.message !== 'cancelled') {
        setError(err.response?.data?.error || err.message || 'Failed to purchase storage');
      }
    } finally {
      setStorageLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/auth/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    window.location.reload();
  };

  const getTierInfo = () => {
    // Check the DB tier first (covers premium emails + Stripe subscribers)
    const dbTier = subscription?.tier?.toLowerCase();
    if (dbTier === 'premium') {
      return { tier: 'Premium', color: 'text-yellow-600', badge: 'bg-gradient-to-r from-yellow-500 to-orange-500' };
    }
    if (dbTier === 'basic') {
      return { tier: 'Basic', color: 'text-purple-600', badge: 'bg-gradient-to-r from-purple-500 to-pink-500' };
    }
    // Fallback: check Stripe subscription details
    const stripeSub = subscription?.subscription;
    if (stripeSub?.status === 'active') {
      if (stripeSub.plan?.includes('premium')) {
        return { tier: 'Premium', color: 'text-yellow-600', badge: 'bg-gradient-to-r from-yellow-500 to-orange-500' };
      }
      if (stripeSub.plan?.includes('basic')) {
        return { tier: 'Basic', color: 'text-purple-600', badge: 'bg-gradient-to-r from-purple-500 to-pink-500' };
      }
    }
    return { tier: 'Free', color: 'text-gray-600', badge: null };
  };

  const tierInfo = getTierInfo();
  const isPaid = subscription?.status === 'active';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-suede-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light text-stone-900 mb-2">My <span className="font-semibold bg-gradient-to-r from-suede-600 to-terracotta-500 bg-clip-text text-transparent">Account</span></h1>
          <p className="text-stone-600">Manage your profile and subscription</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-4 bg-white rounded-2xl border border-red-200 shadow-sm flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Profile Card */}
        <div>
          <Card className="p-6 bg-white rounded-2xl border border-stone-200/60 shadow-sm">
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-suede-600 to-terracotta-500 flex items-center justify-center flex-shrink-0">
                <User className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-semibold text-stone-900 truncate">{user?.name || 'User'}</h2>
                  {tierInfo.badge && (
                    <div className={`px-2.5 py-0.5 ${tierInfo.badge} rounded-full flex-shrink-0`}>
                      <span className="text-white text-xs font-semibold flex items-center gap-1">
                        <Crown className="w-3.5 h-3.5" /> {tierInfo.tier}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-stone-500 flex items-center gap-2 text-sm mt-0.5 truncate">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{user?.email || 'No email'}</span>
                </p>
              </div>
            </div>

            {user?.oauth_provider && (
              <div className="flex items-center gap-2 text-sm text-stone-500 mb-4">
                <Shield className="w-4 h-4" />
                Signed in with {user.oauth_provider.charAt(0).toUpperCase() + user.oauth_provider.slice(1)}
              </div>
            )}

            <div className="border-t border-stone-200/80 pt-4">
              <p className="text-sm text-stone-500">
                Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : 'Unknown'}
              </p>
            </div>
          </Card>
        </div>

        {/* Subscription Card */}
        <div>
          <Card className="p-6 bg-white rounded-2xl border border-stone-200/60 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Subscription</h3>
                <p className="text-sm text-stone-500">Manage your plan</p>
              </div>
            </div>

            <div className="bg-stone-50 rounded-2xl p-4 mb-6 border border-stone-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-stone-600">Current Plan</span>
                <span className={`font-semibold ${tierInfo.color}`}>
                  {tierInfo.tier} Plan
                </span>
              </div>
              
              {isPaid && subscription?.currentPeriodEnd && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">Renews On</span>
                  <span className="text-sm text-stone-900">
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}

              {subscription?.cancelAtPeriodEnd && (
                <div className="mt-2 p-2 bg-suede-50 rounded-2xl border border-suede-200">
                  <p className="text-sm text-suede-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Subscription will cancel at end of billing period
                  </p>
                </div>
              )}
            </div>

            {/* Plan Features */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-stone-700 mb-3">Your Benefits</h4>
              <div className="space-y-2">
                {tierInfo.tier === 'Premium' ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-yellow-500" />
                      Unlimited wardrobe items, AI chats & wishlist
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-yellow-500" />
                      40 standard try-ons/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-yellow-500" />
                      AI Studio: 4 fashion videos, 2 360° views, 10 creative visions
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-yellow-500" />
                      6 bulk uploads, 25 image edits/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-yellow-500" />
                      60 studio BG removals, 50 background changes
                    </div>
                  </>
                ) : tierInfo.tier === 'Basic' ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-purple-500" />
                      Up to 600 wardrobe items, 25 wishlist/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-purple-500" />
                      30 AI chats, 15 standard try-ons/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-purple-500" />
                      AI Studio: 2 fashion videos, 1 360° view, 3 creative visions
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-purple-500" />
                      3 bulk uploads, 15 image edits/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-purple-500" />
                      35 studio BG removals, 30 background changes
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-olive-500" />
                      Up to 200 wardrobe items, 10 wishlist/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-olive-500" />
                      1 bulk upload, 1 try-on, 1 image edit/month
                    </div>
                    <div className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-olive-500" />
                      15 studio BG removals, 10 BG changes/month
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Compare Plans */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-stone-700 mb-3">Compare Plans</h4>
              <div className="space-y-3">
                {/* Free Tier */}
                <div className={`p-3 rounded-xl border transition-all ${
                  tierInfo.tier === 'Free' ? 'border-olive-300 bg-olive-50/50' : 'border-stone-200 bg-white/50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-800">Free</span>
                      {tierInfo.tier === 'Free' && <span className="text-[10px] bg-olive-500 text-white px-2 py-0.5 rounded-full font-medium">Current</span>}
                    </div>
                    <span className="text-sm font-bold text-stone-700">$0</span>
                  </div>
                  <div className="text-xs text-stone-500 space-y-1">
                    <p>• Up to 200 wardrobe items, 10 wishlist/mo</p>
                    <p>• 1 bulk upload, 1 try-on, 1 image edit/mo</p>
                    <p>• 15 studio BG removals, 10 BG changes/mo</p>
                    <p>• Unlimited calendar, canvas & AI Detect</p>
                  </div>
                </div>

                {/* Basic Tier */}
                <div className={`p-3 rounded-xl border transition-all ${
                  tierInfo.tier === 'Basic' ? 'border-purple-300 bg-purple-50/50' : 'border-stone-200 bg-white/50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-800">Basic</span>
                      {tierInfo.tier === 'Basic' && <span className="text-[10px] bg-purple-500 text-white px-2 py-0.5 rounded-full font-medium">Current</span>}
                    </div>
                    <span className="text-sm font-bold text-stone-700">€7.99<span className="text-xs font-normal text-stone-400">/mo</span></span>
                  </div>
                  <div className="text-xs text-stone-500 space-y-1">
                    <p>• Up to 600 wardrobe items, 25 wishlist/mo</p>
                    <p>• 30 AI chats, 15 try-ons/mo</p>
                    <p>• AI Studio: 2 videos, 1 360°, 3 creative visions</p>
                    <p>• 3 bulk uploads, 15 image edits/mo</p>
                    <p>• 35 studio BG removals, 30 BG changes/mo</p>
                  </div>
                  {tierInfo.tier === 'Free' && (
                    <Button onClick={handleUpgrade} size="sm" className="w-full mt-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs rounded-xl">
                      <Crown className="w-3 h-3 mr-1" /> Upgrade to Basic
                    </Button>
                  )}
                </div>

                {/* Premium Tier */}
                <div className={`p-3 rounded-xl border transition-all ${
                  tierInfo.tier === 'Premium' ? 'border-yellow-300 bg-yellow-50/50' : 'border-stone-200 bg-white/50'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-800">Premium</span>
                      {tierInfo.tier === 'Premium' && <span className="text-[10px] bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-2 py-0.5 rounded-full font-medium">Current</span>}
                    </div>
                    <span className="text-sm font-bold text-stone-700">€14.99<span className="text-xs font-normal text-stone-400">/mo</span></span>
                  </div>
                  <div className="text-xs text-stone-500 space-y-1">
                    <p>• Unlimited wardrobe items, AI chats & wishlist</p>
                    <p>• 40 try-ons, 6 bulk uploads/mo</p>
                    <p>• AI Studio: 4 videos, 2 360°, 10 creative visions</p>
                    <p>• 25 image edits, 60 studio BG removals/mo</p>
                    <p>• 50 background changes/mo</p>
                  </div>
                  {(tierInfo.tier === 'Free' || tierInfo.tier === 'Basic') && (
                    <Button onClick={handleUpgrade} size="sm" className="w-full mt-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs rounded-xl">
                      <Crown className="w-3 h-3 mr-1" /> Upgrade to Premium
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {isPaid ? (
                <>
                  <Button
                    onClick={handleManageSubscription}
                    disabled={portalLoading}
                    className="w-full bg-gradient-to-r from-suede-600 to-terracotta-500 hover:from-suede-700 hover:to-terracotta-600 text-white rounded-2xl"
                  >
                    {portalLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Manage Subscription
                  </Button>
                  {tierInfo.tier === 'Basic' && (
                    <Button
                      onClick={handleUpgrade}
                      variant="outline"
                      className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-50 rounded-2xl"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      Upgrade to Premium
                    </Button>
                  )}
                </>
              ) : (
                <Button
                  onClick={handleUpgrade}
                  className="w-full bg-gradient-to-r from-suede-600 to-terracotta-500 hover:from-suede-700 hover:to-terracotta-600 text-white rounded-2xl"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Your Plan
                </Button>
              )}
            </div>
          </Card>
        </div>

        {/* Add-Ons Card — Bulk Top-Up + Storage */}
        <div>
          <Card className="p-6 bg-white rounded-2xl border border-stone-200/60 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-olive-500 to-olive-400 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Add-Ons</h3>
                <p className="text-sm text-stone-500">Extra credits & storage upgrades</p>
              </div>
            </div>

            {/* Bulk Top-Up */}
            <div className="p-4 rounded-xl border border-stone-200 bg-stone-50/50 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-stone-800">Bulk Add Top-Up</h4>
                  <p className="text-xs text-stone-500 mt-0.5">One-time purchase</p>
                </div>
                <span className="text-sm font-bold text-stone-700">
                  {shouldUseStoreKit() ? (getLocalizedPrice('bulk-topup') || '€2.99') : `${getCurrencySymbol(currency)}2.99`}
                </span>
              </div>
              <div className="text-xs text-stone-500 space-y-1 mb-3">
                <p>• +5 bulk uploads (each up to 15 items)</p>
                <p>• Use them anytime — they don't expire</p>
              </div>
              <Button
                onClick={handleBuyBulkTopup}
                disabled={bulkLoading}
                size="sm"
                className="w-full bg-gradient-to-r from-olive-600 to-olive-500 text-white text-xs rounded-xl"
              >
                {bulkLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Sparkles className="w-3 h-3 mr-1" />
                )}
                Buy Top-Up Pack
              </Button>
            </div>

            {/* Storage Tiers */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-stone-700">Storage Add-Ons</h4>
              <p className="text-xs text-stone-400">For Free & Basic plan users who need more wardrobe capacity. Premium already includes unlimited storage.</p>
              
              <div className="p-3 rounded-xl border border-stone-200 bg-white/50">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-stone-800">Storage Tier 1</span>
                  </div>
                  <span className="text-sm font-bold text-stone-700">
                    {shouldUseStoreKit() ? (getLocalizedPrice('storage-tier1') || '€0.99') : `${getCurrencySymbol(currency)}0.99`}
                    <span className="text-xs font-normal text-stone-400">/mo</span>
                  </span>
                </div>
                <p className="text-xs text-stone-500 mb-2">Up to 600 wardrobe items</p>
                <Button
                  onClick={() => handleBuyStorage('tier1')}
                  disabled={storageLoading}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs rounded-xl border-stone-300"
                >
                  {storageLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Subscribe to Tier 1
                </Button>
              </div>

              <div className="p-3 rounded-xl border border-stone-200 bg-white/50">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-stone-800">Storage Tier 2</span>
                  </div>
                  <span className="text-sm font-bold text-stone-700">
                    {shouldUseStoreKit() ? (getLocalizedPrice('storage-tier2') || '€4.99') : `${getCurrencySymbol(currency)}4.99`}
                    <span className="text-xs font-normal text-stone-400">/mo</span>
                  </span>
                </div>
                <p className="text-xs text-stone-500 mb-2">Up to 999 wardrobe items total</p>
                <Button
                  onClick={() => handleBuyStorage('tier2')}
                  disabled={storageLoading}
                  variant="outline"
                  size="sm"
                  className="w-full text-xs rounded-xl border-stone-300"
                >
                  {storageLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  Subscribe to Tier 2
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Settings Card */}
        <div>
          <Card className="p-6 bg-white rounded-2xl border border-stone-200/60 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-suede-500 to-suede-400 flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Settings</h3>
                <p className="text-sm text-stone-500">Account preferences</p>
              </div>
            </div>

            <div className="space-y-2">
              {/* Currency selector */}
              <div className="relative">
                <button
                  onClick={() => setShowCurrencyPicker(!showCurrencyPicker)}
                  className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-white/50 transition-colors"
                >
                  <span className="text-stone-700">Currency</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-suede-600">{getCurrencySymbol(currency)} {currency}</span>
                    <ChevronRight className={`w-5 h-5 text-stone-400 transition-transform ${showCurrencyPicker ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                {showCurrencyPicker && (
                  <div className="overflow-hidden">
                    <div className="grid grid-cols-3 gap-1.5 px-3 pb-3">
                      {getSupportedCurrencies().map(c => (
                        <button
                          key={c.code}
                          onClick={() => {
                            setCurrency(c.code);
                            setCurrencyState(c.code);
                            setShowCurrencyPicker(false);
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-medium transition-all ${
                            currency === c.code
                              ? 'bg-suede-500 text-white'
                              : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                          }`}
                        >
                          <span className="text-sm">{c.symbol}</span>
                          <span>{c.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => navigate('/faq')}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-stone-700">FAQ</span>
                </div>
                <ChevronRight className="w-5 h-5 text-stone-400" />
              </button>

              <button
                onClick={() => navigate('/terms')}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-stone-500" />
                  <span className="text-stone-700">Terms of Service</span>
                </div>
                <ChevronRight className="w-5 h-5 text-stone-400" />
              </button>

              <button
                onClick={() => navigate('/privacy')}
                className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-white/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-stone-500" />
                  <span className="text-stone-700">Privacy Policy</span>
                </div>
                <ChevronRight className="w-5 h-5 text-stone-400" />
              </button>

              {shouldUseStoreKit() && (
                <button
                  onClick={async () => {
                    setRestoreLoading(true);
                    try {
                      await restorePurchases();
                      window.location.reload();
                    } catch (err) {
                      setError(err.message || 'Failed to restore purchases');
                    } finally {
                      setRestoreLoading(false);
                    }
                  }}
                  disabled={restoreLoading}
                  className="w-full flex items-center justify-between p-3 rounded-2xl hover:bg-white/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <RotateCcw className={`w-4 h-4 text-suede-500 ${restoreLoading ? 'animate-spin' : ''}`} />
                    <span className="text-stone-700">Restore Purchases</span>
                  </div>
                  <ChevronRight className="w-5 h-5 text-stone-400" />
                </button>
              )}
            </div>
          </Card>
        </div>

        {/* Logout Button */}
        <div>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full py-6 text-red-600 border-stone-200/80 hover:bg-red-50/50 rounded-2xl bg-white shadow-sm"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>

        {/* Delete Account — Apple Guideline 5.1.1(v) */}
        <div>
          <Card className="p-6 bg-white rounded-2xl border border-red-200/60 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-stone-900">Delete Account</h3>
                <p className="text-sm text-stone-500">Permanently remove your account and data</p>
              </div>
            </div>

            {!showDeleteConfirm ? (
              <Button
                onClick={() => setShowDeleteConfirm(true)}
                variant="outline"
                className="w-full border-red-300 text-red-600 hover:bg-red-50 rounded-2xl"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <p className="text-sm text-red-800 font-medium mb-1">⚠️ This action is permanent</p>
                  <p className="text-xs text-red-700">
                    All your data will be permanently deleted, including your wardrobe items, saved outfits, 
                    AI creations, and subscription. This cannot be undone.
                  </p>
                </div>
                <div>
                  <label className="text-sm text-stone-600 block mb-2">
                    Type <span className="font-bold text-red-600">DELETE</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type DELETE here"
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                    variant="outline"
                    className="flex-1 rounded-2xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-2xl disabled:opacity-50"
                  >
                    {deleteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete Forever
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Pricing Modal */}
      <PricingModal 
        isOpen={showPricingModal} 
        onClose={() => setShowPricingModal(false)} 
      />

      {/* Storage Already Included popup */}
      <Dialog open={showStorageIncluded} onOpenChange={setShowStorageIncluded}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Storage Already Included
            </DialogTitle>
            <DialogDescription className="text-stone-500 pt-2">
              Your {getTierInfo().tier} plan already includes generous wardrobe storage — no need to purchase a storage add-on! 🎉
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setShowStorageIncluded(false)}
              className="w-full bg-gradient-to-r from-terracotta-500 to-suede-500 text-white rounded-xl"
            >
              Got it!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Account;
